import SiteLayout from '@/components/feature/SiteLayout';
import PageBanner from '@/components/feature/PageBanner';
import GuideFaq from './components/GuideFaq';
import { guideToc, guideFaqs } from '@/mocks/home';

const beforeList = [
  '先想清楚一件事：你要反映的是「事实」还是「听说」。前者适合投稿，后者更适合先自行确认。',
  '把你掌握的时间、地点、人物角色写具体，但不要为了显真实而补充自己也不确定的细节。',
  '如有截图或照片，请保留原始文件与拍摄时间，不要先做滤镜或裁剪。',
  '涉及他人的隐私信息（住址、证件、联系方式）请不要填写，编辑不会采用这类内容。',
];

const compareRows = [
  {
    label: '对外称呼',
    anonymous: '内容中不出现任何称呼',
    signed: '使用你填写的化名或称呼',
  },
  {
    label: '联系方式',
    anonymous: '可留可不留，仅编辑可见',
    signed: '建议留下，便于确认表述',
  },
  {
    label: '后续沟通',
    anonymous: '仅通过你留下的渠道单独联系',
    signed: '可在内容发布前参与确认',
  },
  {
    label: '适用情况',
    anonymous: '涉及职场、行业内幕等敏感场景',
    signed: '希望长期投递、建立稳定沟通',
  },
];

const evidenceTiers = [
  {
    level: '可直接进入核实',
    tone: 'secondary',
    icon: 'ri-check-double-line',
    items: ['公开平台上的原始帖子或视频链接', '你本人在现场拍摄的未处理照片', '带时间戳的聊天记录或邮件'],
  },
  {
    level: '需要补充材料',
    tone: 'accent',
    icon: 'ri-question-line',
    items: ['仅有一句转述、没有具体场景', '截图经过多次转发、无法追溯来源', '时间与地点只能给出模糊区间'],
  },
  {
    level: '不予受理',
    tone: 'primary',
    icon: 'ri-forbid-line',
    items: ['涉及未成年人隐私的内容', '要求提供报酬才肯说清细节', '明显拼凑、无法自洽的传闻'],
  },
];

const timeline = [
  { time: '提交当下', text: '线索进入编辑线索库，系统生成线索编号，便于你后续查询。' },
  { time: '12 小时内', text: '编辑完成初步分类，判断是否具备核实路径，决定是否优先跟进。' },
  { time: '36 小时内', text: '给出初步结论：采用、需补充材料，或暂不跟进并说明原因。' },
  { time: '核实完成', text: '整理成稿，涉及具体个人时留出回应窗口，之后按流程发布。' },
];

const privacyPoints = [
  '投稿人的昵称与联系邮箱仅存于编辑内部线索库，不参与任何对外内容生成。',
  '编辑与你沟通时会使用独立的回信渠道，不在公开评论区确认任何投稿关系。',
  '你可以随时通过投稿时留下的邮箱要求删除自己提交的内容，我们会在一个工作日内处理。',
  '线索库中的历史内容会定期清理，未被采用的线索不会长期留存。',
];

const pageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '投稿指南',
  url: 'https://gzruanke.cn/guide',
  inLanguage: 'zh-CN',
  description: '17吃瓜网投稿指南，说明投稿前须知、匿名与署名区别、证据要求、线索处理时间线与隐私保护政策。',
  isPartOf: { '@type': 'WebSite', name: '17吃瓜网', url: 'https://gzruanke.cn/' },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://gzruanke.cn/' },
      { '@type': 'ListItem', position: 2, name: '投稿指南', item: 'https://gzruanke.cn/guide' },
    ],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: guideFaqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const tierTone: Record<string, string> = {
  secondary: 'bg-secondary-100 border-secondary-200 text-secondary-800',
  accent: 'bg-accent-100 border-accent-200 text-accent-800',
  primary: 'bg-primary-100 border-primary-200 text-primary-700',
};

export default function GuidePage() {
  return (
    <SiteLayout>
      <title>投稿指南 - 17吃瓜网匿名投稿规则与线索要求</title>
      <meta
        name="description"
        content="17吃瓜网投稿指南：投稿前须知、匿名投稿与署名投稿的区别、私信爆料渠道、证据与材料要求、线索处理时间线以及隐私保护政策，帮你一次投对。"
      />
      <meta name="keywords" content="17吃瓜网投稿指南,匿名投稿,线索征集,私信爆料,证据要求" />
      <meta name="robots" content="index,follow" />
      <link rel="canonical" href="https://gzruanke.cn/guide" />

      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="17吃瓜网" />
      <meta property="og:title" content="投稿指南 - 17吃瓜网匿名投稿规则与线索要求" />
      <meta property="og:description" content="从投稿前须知到线索处理时间线，一文说清17吃瓜网的投稿规则与证据要求。" />
      <meta property="og:url" content="https://gzruanke.cn/guide" />
      <meta property="og:locale" content="zh_CN" />

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="投稿指南 - 17吃瓜网匿名投稿规则与线索要求" />
      <meta name="twitter:description" content="投稿前须知、匿名与署名区别、证据要求与隐私保护政策完整说明。" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <PageBanner
        eyebrow="投稿前必读"
        title="投稿指南"
        description="这份指南覆盖了从「决定投稿」到「收到回信」的全过程。花五分钟读完，能让你少来回确认好几轮，也能提高线索被采用的概率。"
        breadcrumb={[{ label: '首页', to: '/' }, { label: '投稿指南' }]}
      />

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <aside className="lg:col-span-3">
            <nav aria-label="本页目录" className="lg:sticky lg:top-28">
              <h2 className="text-xs font-bold tracking-widest text-foreground-500 uppercase">本页目录</h2>
              <ul className="mt-4 flex flex-col gap-1">
                {guideToc.map((t) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      className="block px-3 py-2.5 text-sm text-foreground-700 rounded-md hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      {t.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <article className="lg:col-span-9 flex flex-col gap-14">
            <section id="before" className="scroll-mt-28">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">投稿前须知</h2>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
                投稿入口对所有人开放，但我们对「什么样的线索值得跟进」有明确判断。动笔之前，先看这四条。
              </p>
              <ol className="mt-6 flex flex-col gap-4">
                {beforeList.map((item, i) => (
                  <li key={item} className="flex items-start gap-4 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-sm font-bold">
                      {i + 1}
                    </span>
                    <p className="text-sm leading-7 text-foreground-700">{item}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section id="anonymous" className="scroll-mt-28">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">匿名与署名区别</h2>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
                两者在「信息保护」上没有差别，区别在于是否对外公开一个称呼，以及后续沟通的紧密程度。
              </p>
              <div className="mt-6 overflow-x-auto rounded-lg border border-background-200">
                <table className="w-full min-w-[560px] border-collapse text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th scope="col" className="px-5 py-4 text-left font-bold text-foreground-950">对比项</th>
                      <th scope="col" className="px-5 py-4 text-left font-bold text-foreground-950">匿名投稿</th>
                      <th scope="col" className="px-5 py-4 text-left font-bold text-foreground-950">署名投稿</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compareRows.map((row) => (
                      <tr key={row.label} className="border-t border-background-200">
                        <th scope="row" className="px-5 py-4 text-left font-medium text-foreground-700 bg-background-50">{row.label}</th>
                        <td className="px-5 py-4 text-foreground-600">{row.anonymous}</td>
                        <td className="px-5 py-4 text-foreground-600">{row.signed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="private" className="scroll-mt-28">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">私信爆料渠道</h2>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
                如果你更习惯一对一的沟通方式，可以绕过表单，直接通过下面两个渠道把线索发给我们。
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="p-6 rounded-lg bg-secondary-100 border border-secondary-200">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-500 text-background-50">
                    <i className="ri-mail-line text-xl"></i>
                  </span>
                  <h3 className="mt-4 text-base font-bold text-foreground-950">投稿邮箱</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-700">
                    适合材料较多、需要分批说明的情况，邮件标题写明线索类型即可。
                  </p>
                  <p className="mt-4 text-sm font-bold text-secondary-800">tougao@17chigua.example</p>
                </div>
                <div className="p-6 rounded-lg bg-accent-100 border border-accent-200">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                    <i className="ri-message-3-line text-xl"></i>
                  </span>
                  <h3 className="mt-4 text-base font-bold text-foreground-950">站内私信</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-700">
                    适合先探口风、确认某条线索有没有跟进价值，再决定是否完整提交。
                  </p>
                  <p className="mt-4 text-sm font-bold text-accent-800">@17吃瓜网编辑</p>
                </div>
              </div>
            </section>

            <section id="evidence" className="scroll-mt-28">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">证据与材料要求</h2>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
                材料决定核实速度。我们把常见的线索分成三档，你可以对照自己的情况先做判断。
              </p>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
                {evidenceTiers.map((tier) => (
                  <div key={tier.level} className={`p-6 rounded-lg border ${tierTone[tier.tone]}`}>
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-background-50/70">
                      <i className={`${tier.icon} text-xl`}></i>
                    </span>
                    <h3 className="mt-4 text-base font-bold">{tier.level}</h3>
                    <ul className="mt-4 flex flex-col gap-3">
                      {tier.items.map((item) => (
                        <li key={item} className="text-sm leading-6 text-foreground-700">{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            <section id="timeline" className="scroll-mt-28">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">线索处理时间线</h2>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
                从提交到有结论，一条线索通常会经历下面四个节点。
              </p>
              <ol className="mt-6 relative flex flex-col gap-6 pl-6 border-l-2 border-background-200">
                {timeline.map((t) => (
                  <li key={t.time} className="relative">
                    <span className="absolute -left-[31px] top-1 w-3.5 h-3.5 rounded-full bg-primary-500 ring-4 ring-background-50"></span>
                    <h3 className="text-base font-bold text-foreground-950">{t.time}</h3>
                    <p className="mt-1.5 text-sm leading-7 text-foreground-600">{t.text}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section id="privacy" className="scroll-mt-28">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">隐私保护政策</h2>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
                投稿这件事本身就带着风险，所以保护措施必须写在明面上，而不是只靠一句承诺。
              </p>
              <ul className="mt-6 flex flex-col gap-4">
                {privacyPoints.map((p) => (
                  <li key={p} className="flex items-start gap-3.5 p-5 rounded-lg bg-background-100 border border-background-200">
                    <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className="ri-lock-2-line text-lg"></i>
                    </span>
                    <p className="text-sm leading-7 text-foreground-700">{p}</p>
                  </li>
                ))}
              </ul>
            </section>

            <section id="faq" className="scroll-mt-28">
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">常见问题</h2>
              <p className="mt-4 text-sm md:text-base leading-8 text-foreground-600">
                下面这些问题来自真实的投稿咨询，如果你也有类似疑问，先看这里。
              </p>
              <div className="mt-6">
                <GuideFaq />
              </div>
            </section>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}